import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductCarouselProductStyles } from "@tIntermatic/Components/IntProductCarouselProduct";
import CarosulArrow from "@tIntermatic/Icons/CarosulArrow";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface DivisionModel {
    fields: {
        title: string;
        background: "image";
        image: string;
        destination: LinkFieldValue;
        description: string;
        textForLink: string;
        productIds: string;
    };
}

const enum fields {
    divisions = "divisions",
    title = "title",
    maxNumberOfColumns = "maxNumberOfColumns",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.divisions]: DivisionModel[];
        [fields.title]: string;
        [fields.maxNumberOfColumns]: number;
    };
}

export interface IntProductRelatedArticlesCarousalStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    mobileprevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    carouselNavigationWrapper?: InjectableCss;
    prevCarouselIcon?: InjectableCss;
    nextCarouselIcon?: InjectableCss;
    widgetWrapper?: InjectableCss;
    articleTitle?: InjectableCss;
    articleDescp?: InjectableCss;
    articleLink?: InjectableCss;
    articleImage?: InjectableCss;
    articleIcon?: InjectableCss;
}

export const intProductRelatedArticlesCarousalStyles: IntProductRelatedArticlesCarousalStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    articleTitle: {
        css: css`
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 19px;
            padding: 14px 19.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 14px 14px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },

    articleDescp: {
        css: css`
            padding: 0 20px;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            color: #999999;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 54px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                            padding: 0 14px;
                            margin-bottom: 35px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    articleImage: {
        css: css`
            min-height: 343px;
            img {
                height: 100%;
                min-height: 343px;
                object-fit: cover;
            }
        `,
    },
    articleLink: {
        css: css`
            position: absolute;
            bottom: 2px;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
            color: #006bc0;
            padding: 11px 20px 18.5px 20px;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 11px 14px 7px 14px;
                        `,
                        css`
                            padding: 11px 14px 7px 14px;
                        `,
                        css`
                            padding: 11px 14px 7px 14px;
                        `,
                        css`
                            padding: 11px 20px 7px 20px;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    articleIcon: {
        css: css`
            margin: 0 14px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin: 0 5px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
            > svg {
                > g {
                    > g {
                        stroke: #006bc0 !important;
                        stroke-width: 3;
                    }
                }
            }
        `,
    },
    widgetWrapper: {
        css: css`
            padding-bottom: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 -50px;
                    `,
                    css`
                        margin: 0 -50px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 0px;
            color: #33333a;
            letter-spacing: 0;
            flex: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 26px;
                        font-weight: 600;
                        line-height: 31px;
                    `,
                    css`
                        font-size: 26px;
                        font-weight: 600;
                        line-height: 31px;
                    `,
                    css`
                        font-size: 26px;
                        font-weight: 600;
                        line-height: 31px;
                    `,
                    css`
                        font-size: 30px;
                        font-weight: 500;
                        line-height: 60px;
                    `,
                    css`
                        font-size: 30px;
                        font-weight: 500;
                        line-height: 60px;
                    `,
                ])}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin-bottom: 50px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            margin-right: 13px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mobileprevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > span {
                            padding-top: 10px;
                        }
                    `,
                    css`
                        > span {
                            padding-top: 10px;
                        }
                    `,
                    css`
                        > span {
                            padding-top: 10px;
                        }
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > span {
                            padding-bottom: 12px;
                        }
                    `,
                    css`
                        > span {
                            padding-bottom: 12px;
                        }
                    `,
                    css`
                        > span {
                            padding-bottom: 12px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            margin: 0 -14.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 541px;
                        max-width: 315px;
                        margin: 0 auto;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 14.5px;
                    `,
                    css`
                        padding: 0 14.5px;
                    `,
                    css`
                        padding: 0 14.5px;
                    `,
                    css`
                        padding: 0 14.5px;
                    `,
                    css`
                        padding: 0 14.5px;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            min-height: 523px;
            height: 100%;
            background-color: #ffffff;
            border: 1px solid #e1e1e1;
        `,
    },

    carouselNavigationWrapper: {
        css: css`
            display: flex;
            margin-top: 50px;
            padding: 0 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                        margin-bottom: 4px;
                    `,
                    css`
                        padding: 0 20px;
                        margin-bottom: 4px;
                    `,
                    css`
                        padding: 0 20px;
                        margin-bottom: 4px;
                    `,
                    css`
                        margin-bottom: 21px;
                    `,
                    css`
                        margin-bottom: 21px;
                    `,
                ])}
        `,
    },

    prevCarouselIcon: {
        css: css`
            transform: rotate(180deg);
            > svg {
                > g {
                    stroke-width: 1.1;
                }
            }
            &:hover {
                > svg {
                    #Product-Detail_Default {
                        stroke: #006bc0;
                    }
                }
            }
        `,
    },

    nextCarouselIcon: {
        css: css`
            &:hover {
                > svg {
                    #Product-Detail_Default {
                        stroke: #006bc0;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > svg {
                            height: 20px;
                            width: 20px;
                            > g {
                                stroke-width: 2;
                            }
                        }
                    `,
                    css`
                        > svg {
                            height: 20px;
                            width: 20px;
                            > g {
                                stroke-width: 2;
                            }
                        }
                    `,
                    css`
                        > svg {
                            height: 20px;
                            width: 20px;
                            > g {
                                stroke-width: 2;
                            }
                        }
                    `,
                    css`
                        > svg {
                            > g {
                                stroke-width: 1.1;
                            }
                        }
                    `,
                    css`
                        > svg {
                            > g {
                                stroke-width: 1.1;
                            }
                        }
                    `,
                ])}
        `,
    },
};

const styles = intProductRelatedArticlesCarousalStyles;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.divisions.map(division => getLink(state, division.fields.destination)),
    };
};

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext & HasProduct & ReturnType<typeof mapStateToProps>;

const IntProductRelatedArticlesCarousal: React.FC<Props> = ({ id, theme, fields, shellContext, product, pageLink }) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    let isShow = false;
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !fields.divisions || fields.divisions.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: fields.divisions.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, fields.divisions]);

    if (fields.divisions && fields.divisions.length > 0) {
        fields.divisions.forEach(slide => {
            if (slide.fields.productIds) {
                if (slide.fields.productIds.split(",").includes(product.productNumber)) {
                    isShow = true;
                }
            }
        });
    }

    if (!fields.divisions || fields.divisions.length === 0) {
        return null;
    }

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {fields.divisions && fields.divisions.length && isShow ? (
                <StyledWrapper {...styles.carouselNavigationWrapper}>
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        {fields.divisions.length > slidesToScroll && (
                            <Button
                                {...styles.mobileprevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon
                                    src={CarosulArrow}
                                    css={css`
                                        transform: rotate(180deg);
                                        > svg {
                                            height: 20px;
                                            width: 20px;
                                            > g {
                                                stroke-width: 2;
                                            }
                                        }
                                        &:hover {
                                            > svg {
                                                #Product-Detail_Default {
                                                    stroke: #006bc0;
                                                }
                                            }
                                        }
                                    `}
                                />
                            </Button>
                        )}
                    </StyledWrapper>
                    {fields.divisions.length > 0 && isShow ? (
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                    ) : null}
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        {fields.divisions.length > slidesToScroll ? (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={CarosulArrow} {...styles.prevCarouselIcon} />
                            </Button>
                        ) : null}
                        {fields.divisions.length > slidesToScroll ? (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={CarosulArrow} {...styles.nextCarouselIcon} />
                            </Button>
                        ) : null}
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}

            {fields.divisions && fields.divisions.length && isShow ? (
                <GridContainer {...styles.mainContainer} data-test-selector="Related Articles">
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {fields.divisions && fields.divisions.length > 0
                                        ? fields.divisions.map((slide, index) => {
                                              return slide.fields.productIds &&
                                                  slide.fields.productIds.split(",").includes(product.productNumber) ? (
                                                  <StyledWrapper
                                                      // eslint-disable-next-line react/no-array-index-key
                                                      key={index}
                                                      {...styles.carouselSlide}
                                                      style={{ width: `calc(100% / ${slidesToScroll})` }}
                                                  >
                                                      <StyledWrapper
                                                          {...styles.carouselSlideInner}
                                                          data-test-selector="articleContainer"
                                                      >
                                                          <StyledWrapper>
                                                              <LazyImage
                                                                  {...styles.articleImage}
                                                                  src={slide.fields.image}
                                                                  altText={slide.fields.title}
                                                              />
                                                          </StyledWrapper>
                                                          <StyledWrapper {...styles.articleTitle}>
                                                              {slide.fields.title}
                                                          </StyledWrapper>
                                                          <StyledWrapper {...styles.articleDescp}>
                                                              {slide.fields.description}
                                                          </StyledWrapper>
                                                          <Link href={pageLink[index]?.url} {...styles.articleLink}>
                                                              {slide.fields.textForLink}
                                                              <Icon
                                                                  src={CarosulArrow}
                                                                  size={10}
                                                                  {...styles.articleIcon}
                                                              ></Icon>
                                                          </Link>
                                                      </StyledWrapper>
                                                  </StyledWrapper>
                                              ) : null;
                                          })
                                        : null}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            ) : null}

            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(withTheme(withProduct(IntProductRelatedArticlesCarousal)))),
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "textForLink",
                        editorTemplate: "TextField",
                        defaultValue: "View All",
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "description",
                    },
                    {
                        name: "productIds",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        displayName: "Product Ids",
                        placeholder: "Enter Product Ids",
                    },
                ],
            },
            {
                name: "Title",
                editorTemplate: "TextField",
                defaultValue: fields.title,
                fieldType: "Translatable",
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
